import { useEffect, useState } from 'react';
import 'react-calendar/dist/Calendar.css';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';
import {
  PrimaryTextColor,
  PrimaryTitleGray,
} from '../../../app/theme/tokens/colors';
import { TextAreaInput } from '../../../shared/ui/Inputs';
import { ImageSlider } from '../../../shared/media/slider';
import { getProductsImages } from '../../../services/products/productService';
import { saveRechargerOrder } from '../../../services/recharge/rechargeService';
import { usePortalStore } from '../../../stores/portalStore';
import { useRechargeStore } from '../store/rechargeStore';
import { DefaultFinishOrder } from '../../orders/usecases/finish/defaultFinishOrder';
import { getBase64Img } from '../../../utils/format';
import { GiHandTruck } from 'react-icons/gi';

export const FinishRechargerOrder = () => {
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1224px)' });
  const currentUser = usePortalStore((state) => state.currentUser);
  const setCurrentAlertError = usePortalStore(
    (state) => state.setCurrentAlertError
  );

  const selectedPatient = useRechargeStore((state) => state.selectedPatient);
  const rechargerActions = useRechargeStore((state) => state.rechargerActions);
  const selectedPatientIndex = useRechargeStore(
    (state) => state.selectedPatientIndex
  );
  const navigate = useNavigate();
  const operations =
    rechargerActions &&
    selectedPatientIndex !== undefined &&
    selectedPatientIndex !== null
      ? rechargerActions[selectedPatientIndex] ?? {}
      : {};
  const operationValues = Object.values(operations ?? {});

  // Resumo final deve ser sempre "Recarga" (sem expor lógica de troca)
  const resolvedOrderType = 'Recarga';
  const fastDeliveryLabel = 'Recarga imediata';
  const scheduleDeliveryLabel = 'Recarga agendada';

  const [techDetails, setTechDetails] = useState('');
  const [image, setImage] = useState([]);

  const getCityLabel = () => {
    const city = selectedPatient?.city;
    if (typeof city === 'object') {
      return city?.name ?? '-';
    }
    return city ?? '-';
  };

  const formatVolume = (raw) => {
    if (raw === null || raw === undefined || raw === '') return '-';
    const s = String(raw).trim();
    if (!s) return '-';
    const normalized = s.replace('m3', 'm³').replace('m³', '').trim();
    const asNumber = Number(normalized.replace(',', '.'));
    const text = Number.isFinite(asNumber)
      ? String(asNumber).replace('.', ',')
      : normalized;
    return `${text} m³`;
  };

  const finishOrder = async (deliveryDate, deliveryTurn) => {
    const orderNumber = await saveRechargerOrder(
      currentUser,
      selectedPatient.cnpj,
      selectedPatient,
      operations,
      deliveryDate,
      techDetails,
      deliveryTurn
    );
    if (orderNumber === null) {
      setCurrentAlertError(
        `Nenhuma filial encontrada para a cidade de ${getCityLabel()}`
      );
    }
    return orderNumber;
  };

  useEffect(() => {
    (async () => {
      if (!selectedPatient) {
        console.warn(
          '[FinishRechargerOrder] selectedPatient missing; redirecting back'
        );
        navigate('/recharge/rechargePatient?keepState=true');
        return;
      }

      const ids = operationValues
        .map((x) => x?.cylinder?.id)
        .filter((x) => x !== undefined && x !== null);

      if (ids.length === 0) {
        setImage([]);
        return;
      }

      const img = await getProductsImages(ids);
      setImage([...img]);
    })();
  }, [selectedPatientIndex, rechargerActions, selectedPatient]);

  if (!selectedPatient) {
    return null;
  }

  const topTile = () => {
    const cylindersToShow = operationValues
      .filter(
        (x) =>
          (x?.action === 'recarga' && (x?.quantity ?? 0) > 0) ||
          (x?.action === 'troca' && x?.newCylinder !== undefined)
      )
      .map((x) => {
        const isExchange =
          x?.action === 'troca' && x?.newCylinder !== undefined;
        const description = isExchange
          ? x?.newCylinder?.description
          : x?.cylinder?.product;
        const size = isExchange ? x?.newCylinder?.size : x?.cylinder?.size;
        const volumeLabel = formatVolume(size);
        return {
          description: description ?? '-',
          volumeLabel,
          quantity: x?.quantity ?? 0,
        };
      })
      .filter((x) => (x?.quantity ?? 0) > 0);

    return (
      <div
        style={{
          display: isTabletOrMobile ? 'block' : 'flex',
          width: '100%',
          gap: isTabletOrMobile ? '16px' : '28px',
          alignItems: 'flex-start',
        }}
      >
        <div style={{ flex: '1 1 260px', minWidth: 0 }}>
          <ImageSlider images={image.map(getBase64Img)}></ImageSlider>
        </div>
        <div
          style={{
            flex: '2 1 420px',
            minWidth: 0,
            marginTop: isTabletOrMobile ? '16px' : '0px',
            color: PrimaryTextColor,
          }}
        >
          {cylindersToShow.length > 0 ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                borderBottom: 'solid 1px #ccc',
                paddingBottom: '8px',
                marginBottom: '10px',
                marginTop: '0px',
              }}
            >
              <GiHandTruck
                style={{
                  fontSize: '30px',
                  marginRight: '9px',
                  color: '#318300',
                }}
              ></GiHandTruck>
              <div style={{ fontSize: '16px', color: '#000' }}>
                Cilindros do pedido
              </div>
            </div>
          ) : (
            <></>
          )}
          <ul style={{ margin: '0px', padding: '0px' }}>
            {cylindersToShow.map((x, idx) => (
              <li
                style={{
                  listStyle: 'none',
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'flex-start',
                  gap: '6px 14px',
                  padding: '10px 14px',
                  background: '#efefef',
                  borderRadius: '8px',
                  marginBottom: '8px',
                  color: '#000',
                }}
                key={idx}
              >
                <div style={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
                  {x.description}
                </div>
                <div
                  style={{
                    whiteSpace: isTabletOrMobile ? 'normal' : 'nowrap',
                    color: PrimaryTitleGray,
                    fontWeight: 600,
                  }}
                >{`Volume: ${x.volumeLabel} | Quantidade: ${x.quantity}`}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };
  const bottomTile = () => {
    return (
      <div style={{ width: '100%' }}>
        <h4
          style={{
            color: PrimaryTitleGray,
            margin: '0px',
            marginBottom: '10px',
          }}
        >
          Informações adicionais
        </h4>
        <TextAreaInput
          onChange={(e) => setTechDetails(e.target.value)}
          style={{ minWidth: '100%', minHeight: '120px', margin: '0px' }}
        ></TextAreaInput>
      </div>
    );
  };
  return (
    <DefaultFinishOrder
      selectedPatient={selectedPatient}
      topTileRender={topTile}
      bottomTileRender={bottomTile}
      orderType={resolvedOrderType}
      onFinish={finishOrder}
      fastDeliveryLabel={fastDeliveryLabel}
      scheduleDeliveryLabel={scheduleDeliveryLabel}
      loadPatientDetails={true}
      patientDetailsLookup="sap"
    ></DefaultFinishOrder>
  );
};
